import { useCallback, useEffect, useMemo, useState } from 'react';

import defaultCarousel1 from '../assets/landing/direct_reg_carousel_1.webp';
import defaultCarousel2 from '../assets/landing/direct_reg_carousel_2.webp';
import defaultCarousel3 from '../assets/landing/direct_reg_carousel_3.webp';
import { useFlag } from '../services/launchDarkly';
import { useStores } from '../stores/useStores';

const DEFAULT_PRIMARY_IMAGE = defaultCarousel1;
const DEFAULT_SECONDARY_CAROUSEL_IMAGES = [defaultCarousel2, defaultCarousel3];
const DIRECT_REG_IMAGES_KEY = 'direct_registration_landing_images';
const IMAGE_VALIDATION_TIMEOUT_MS = 3000;
const CAROUSEL_AUTO_ROTATION_INTERVAL_MS = 4000;
const CAROUSEL_MANUAL_INTERACTION_DELAY_MS = 10000;

interface CarouselImage {
  url: string;
  isValid: boolean;
  isLoaded: boolean;
}

export const useDirectRegCarousel = () => {
  const { authStore } = useStores();

  const [carouselImages, setCarouselImages] = useState<CarouselImage[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoRotating, setIsAutoRotating] = useState(true);
  const directRegGeneralImages = useFlag<string[]>(DIRECT_REG_IMAGES_KEY, []);

  const landingBannerImage = authStore.getDirectSignUpLanderBanner();
  const primaryImage = landingBannerImage || DEFAULT_PRIMARY_IMAGE;

  const allImages = useMemo(() => {
    const validUrls = directRegGeneralImages.filter((url) => url.startsWith('https://') || url.startsWith('/'));
    const images = validUrls.length > 0 ? validUrls : DEFAULT_SECONDARY_CAROUSEL_IMAGES;
    const secondaryImages = images.length <= 2 ? images : images.slice(1, images.length); // if more than 2 images, assume first is primary
    return [primaryImage, ...secondaryImages];
  }, [directRegGeneralImages, primaryImage]);

  const getCarouselUrls = useCallback((): string[] => {
    if (!Array.isArray(directRegGeneralImages)) {
      console.warn('direct_reg_images flag is not an array, using defaults');
      return allImages;
    }

    if (directRegGeneralImages.length === 0) {
      console.info('direct_reg_images flag is empty, using defaults');
      return allImages;
    }

    return allImages;
  }, [allImages, directRegGeneralImages]);

  const validateImage = useCallback((url: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      const timeout = setTimeout(() => {
        resolve(false);
      }, IMAGE_VALIDATION_TIMEOUT_MS);

      img.onload = () => {
        clearTimeout(timeout);
        resolve(true);
      };

      img.onerror = () => {
        clearTimeout(timeout);
        resolve(false);
      };

      img.src = url;
    });
  }, []);

  useEffect(() => {
    const initializeCarousel = async () => {
      const urls = getCarouselUrls();

      const validatedImages = await Promise.all(
        urls.map(async (url): Promise<CarouselImage> => {
          const isValid = await validateImage(url);
          return {
            url,
            isValid,
            isLoaded: isValid,
          };
        }),
      );

      const primaryBanner = validatedImages[0];
      if (primaryBanner && !primaryBanner.isValid) {
        primaryBanner.url = DEFAULT_PRIMARY_IMAGE;
        primaryBanner.isValid = true;
        primaryBanner.isLoaded = true;
      }

      const validImages = validatedImages.filter((img) => img.isValid);

      if (validImages.length === 0) {
        const defaultImages: CarouselImage[] = allImages.map((url) => ({
          url,
          isValid: true,
          isLoaded: true,
        }));

        setCarouselImages(defaultImages);
      } else {
        setCarouselImages(validImages);
      }
    };

    void initializeCarousel();
  }, [getCarouselUrls, validateImage, allImages]);

  useEffect(() => {
    if (!isAutoRotating || carouselImages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % carouselImages.length);
    }, CAROUSEL_AUTO_ROTATION_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isAutoRotating, carouselImages.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
    setIsAutoRotating(false);

    setTimeout(() => setIsAutoRotating(true), CAROUSEL_MANUAL_INTERACTION_DELAY_MS);
  }, []);

  const nextSlide = useCallback(() => {
    const newIndex = (currentIndex + 1) % carouselImages.length;
    goToSlide(newIndex);
  }, [currentIndex, carouselImages.length, goToSlide]);

  const prevSlide = useCallback(() => {
    const newIndex = currentIndex === 0 ? carouselImages.length - 1 : currentIndex - 1;
    goToSlide(newIndex);
  }, [currentIndex, carouselImages.length, goToSlide]);

  return {
    carouselImages: carouselImages.map((img) => img.url),
    currentIndex,
    currentImage: carouselImages[currentIndex]?.url || allImages[0],
    goToSlide,
    nextSlide,
    prevSlide,
    hasMultipleImages: carouselImages.length > 1,
  };
};
